/**
 * StartNextSubmissionDialog — the "Start Next" control for a PCO's submissions.
 *
 * One job: open the PCO's next submission as its new collecting home. It
 * closes nothing and carries nothing. The package that was collecting before
 * stays open and ends the way every package ends — through Approve & Send on
 * its last unsent week.
 *
 * That split (2026-07-29, client) replaced two overlapping flows: close-and-
 * next, which closed the current package and dragged its unsent weeks along,
 * and open-alongside, which was the same dialog with a "Keep … open" checkbox.
 * Both are gone from this surface — the checkbox, the carry-count line, and
 * the branch behind them — because "start the next one" and "finish this one"
 * are separate decisions the PM makes at separate moments.
 *
 * It opens prefilled and fully editable: next number suggested, MOD and FWA
 * inherited from the package it follows. With no open submission to inherit
 * from (the last one closed on send) it still opens — that's precisely when a
 * PCO needs it — just with blank references.
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { normalizeModNumber } from '@coffman-tm/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useToast } from './ToastProvider';
import { formatPcoNumber, formatSubmissionShort, gcName } from '../lib/format';
import { submissionErrorMessage, submissionFromResponse } from '../lib/useSubmissions';
import type { SubmissionSummary } from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

interface Props {
  pcoId: string;
  /** Parent PCO number, for label rendering ("004.3"). */
  pcoParentNumber: string;
  /**
   * EVERY submission on the PCO, any status. The dialog derives two different
   * things from it and they are not the same submission:
   *
   *   - the reference source: the highest-numbered package, whatever its
   *     status. This is what MOD/FWA carry from. Reading them off the open
   *     default instead was the 002.12 bug — right after a package closes on
   *     send there IS no open one, so the dialog birthed the next package with
   *     blank references at the exact moment the workflow calls for it.
   *   - the collecting package: an open one, if any. Only this may be
   *     described as "stays open"; the reference source frequently is closed.
   */
  submissions: SubmissionSummary[];
  onClose: () => void;
  /** Receives the submission that was opened, when the server returns it. */
  onDone: (opened: SubmissionSummary | null) => Promise<void>;
}

/** Numeric order — "10" sorts above "9", which string order gets wrong. */
function numberOf(s: SubmissionSummary): number {
  return Number.parseInt(s.submission_number, 10) || 0;
}

export function StartNextSubmissionDialog({
  pcoId,
  pcoParentNumber,
  submissions,
  onClose,
  onDone,
}: Props) {
  const { token, me } = useAuth();
  const toast = useToast();

  // Highest-numbered package on the PCO regardless of status — the reference
  // source. Computed once at mount: these are prefills the PM may edit, and
  // re-deriving them under a refetch would silently overwrite their typing.
  const [refSource] = useState<SubmissionSummary | null>(() =>
    submissions.reduce<SubmissionSummary | null>(
      (best, s) => (best === null || numberOf(s) > numberOf(best) ? s : best),
      null,
    ),
  );
  const [suggestedNumber] = useState(() => String((refSource ? numberOf(refSource) : 0) + 1));
  // The package still collecting, if there is one. Separate question from the
  // reference source, and the only one that can be called "open".
  const collecting =
    submissions.find((s) => s.status === 'open' && s.is_default_target) ??
    submissions.find((s) => s.status === 'open') ??
    null;

  const [nextNumber, setNextNumber] = useState(suggestedNumber);
  const [modNumber, setModNumber] = useState(refSource?.mod_number ?? '');
  const [fwa, setFwa] = useState(refSource?.hoffman_fwa ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Whether each field arrived prefilled. An empty field that WAS prefilled
  // means the PM deliberately cleared it — send explicit null. An empty field
  // that was never prefilled means "no opinion" — send undefined and let the
  // server's own inheritance decide. Sending null for both was how a blank
  // dialog actively wiped references the server would have carried forward.
  const modWasPrefilled = (refSource?.mod_number ?? '') !== '';
  const fwaWasPrefilled = (refSource?.hoffman_fwa ?? '') !== '';

  const refShort = refSource
    ? formatSubmissionShort(pcoParentNumber, refSource.submission_number)
    : null;
  const collectingShort = collecting
    ? formatSubmissionShort(pcoParentNumber, collecting.submission_number)
    : null;
  const nextShort = formatSubmissionShort(
    pcoParentNumber,
    nextNumber.trim() || suggestedNumber,
  );

  // Mounted only while open, so the lock is unconditional.
  useBodyScrollLock(true);

  // Esc to close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const submit = async () => {
    if (!token) return;
    if (nextNumber.trim() === '') {
      setErr('Enter a submission number.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const trimmedMod = modNumber.trim();
      const trimmedFwa = fwa.trim();
      const res = await api.submissions.startNext(token, pcoId, {
        number: nextNumber.trim(),
        // Cleared-by-hand → null (a real instruction). Never-filled → omitted,
        // so the server can inherit. See modWasPrefilled above.
        ...(trimmedMod !== ''
          ? { mod_number: trimmedMod }
          : modWasPrefilled
            ? { mod_number: null }
            : {}),
        ...(trimmedFwa !== ''
          ? { hoffman_fwa: trimmedFwa }
          : fwaWasPrefilled
            ? { hoffman_fwa: null }
            : {}),
      });
      const opened = submissionFromResponse(res);
      toast.push({
        variant: 'success',
        title: 'Started',
        // Only claim "stays open" about a package that IS open.
        body: collectingShort
          ? `${nextShort} is now collecting. ${collectingShort} stays open until it's sent.`
          : `${nextShort} is now collecting.`,
        durationMs: 6000,
      });
      await onDone(opened);
      onClose();
    } catch (e) {
      setErr(submissionErrorMessage(e, 'Could not start the next submission.', gcName(me)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Start the next submission"
    >
      <div
        className="new-pco-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
      >
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Start next submission</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          {/* Two independent facts, stated separately because they are about
              two different packages: what happens to whatever is still open,
              and where the references came from. */}
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5, margin: 0 }}>
            Opens <strong>{nextShort}</strong> as the new collecting submission — new work
            lands there.{' '}
            {collectingShort ? (
              <>
                <strong>{collectingShort}</strong> stays open and its weeks close out through
                Approve &amp; Send.{' '}
              </>
            ) : null}
            {refShort ? (
              <>MOD and FWA carried from {refShort} — edit them if they changed.</>
            ) : (
              <>This is the PCO&rsquo;s first submission, so MOD and FWA start blank.</>
            )}
          </p>

          <div className="new-pco-field">
            <label className="field-label">Number</label>
            <div className="field-input-group">
              <span className="field-prefix" aria-hidden>
                {formatPcoNumber(pcoParentNumber)}.
              </span>
              <input
                className="field-input mono"
                value={nextNumber}
                onChange={(e) => setNextNumber(e.target.value)}
                autoFocus
                disabled={busy}
                aria-label={`Submission number (after ${formatPcoNumber(pcoParentNumber)}.)`}
              />
            </div>
          </div>
          <div className="new-pco-field">
            <label className="field-label">MOD</label>
            <input
              className="field-input mono"
              value={modNumber}
              onChange={(e) => setModNumber(e.target.value)}
              onBlur={() => {
                // Reflect the API's storage normalization (zero-padded 3
                // digits) so the PM sees on-screen what will actually save.
                const normalized = normalizeModNumber(modNumber) ?? '';
                if (normalized !== modNumber) setModNumber(normalized);
              }}
              disabled={busy}
            />
          </div>
          <div className="new-pco-field">
            <label className="field-label">FWA</label>
            <input
              className="field-input mono"
              value={fwa}
              onChange={(e) => setFwa(e.target.value)}
              disabled={busy}
            />
          </div>

          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{err}</div>}

          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-green" onClick={submit} disabled={busy}>
              {busy ? 'Starting…' : `Start ${nextShort}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
